'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'

// walrus
import { accessibilityLabel } from '../../kit/_support'
import Box from '../../kit/box'
import { BoxLink } from '../../kit/link'
import Pressable from '../../kit/pressable'
import SVG from '../../kit/svg'
import Text from '../../kit/text'

// lib
import { ThemeColor, useHover } from '../_support'
import { NAV_ICONS } from './icons'

/* -----------------------------------------------------------------------------
 * nav items
 * -------------------------------------------------------------------------- */

export type NavItem = {
  key: string
  label: string
  path: string
}

export const navItems: NavItem[] = [
  { key: 'dashboard', label: 'Dashboard', path: '/dashboard' },
  { key: 'home', label: 'Reports', path: '/home' },
  { key: 'opportunities', label: 'Opportunities', path: '/opportunities' },
  { key: 'members', label: 'Members', path: '/members' },
  { key: 'chat', label: 'Chat', path: '/chat' },
  { key: 'settings', label: 'Settings', path: '/settings' }
]

/* -----------------------------------------------------------------------------
 * SideNavLink
 *
 * Compact swaps the dot and label for the glyph, sized to the label's line
 * height so the row keeps exactly the same height either way.
 * -------------------------------------------------------------------------- */

const ICON_SIZE = 18

type SideNavLinkProps = {
  compact?: boolean
  isActive: boolean
  item: NavItem
  onActivePress?: () => void
}

function SideNavLink({
  compact,
  isActive,
  item,
  onActivePress
}: SideNavLinkProps) {
  const { hoverProps, isHovered } = useHover()

  const bg: ThemeColor = isActive
    ? 'proAccentTint'
    : isHovered
    ? 'proNavHover'
    : 'proSurface'
  const tint: ThemeColor = isActive
    ? 'proAccent'
    : isHovered
    ? 'proInk'
    : 'proTextMuted'

  const containerProps = {
    ...accessibilityLabel(`${item.label} Link`, item.label),
    alignItems: 'center' as const,
    bg,
    borderRadius: 'proMd' as const,
    flexDirection: 'row' as const,
    gap: '12' as const,
    justifyContent: compact ? ('center' as const) : ('flex-start' as const),
    px: '12' as const,
    py: '08' as const,
    ...hoverProps
  }

  const content = compact ? (
    <SVG
      fill={tint}
      height={ICON_SIZE}
      stroke={tint}
      svg={NAV_ICONS[item.key]}
      width={ICON_SIZE}
    />
  ) : (
    <>
      <Box
        bg={isActive ? 'proAccent' : 'proTextDisabled'}
        borderRadius='proPill'
        flex='0 0 auto'
        height={6}
        width={6}
      />
      <Text
        font='pro.text.13.medium'
        textColor={isActive || isHovered ? 'proInk' : 'proTextMuted'}
      >
        {item.label}
      </Text>
    </>
  )

  // the active entry becomes a button when the screen wants to handle it
  // itself, which is how chat reaches its member list from the rail
  return isActive && onActivePress ? (
    <Pressable {...containerProps} onPress={onActivePress}>
      {content}
    </Pressable>
  ) : (
    <BoxLink {...containerProps} path={item.path}>
      {content}
    </BoxLink>
  )
}

/* -----------------------------------------------------------------------------
 * SideNav
 * -------------------------------------------------------------------------- */

export type SideNavProps = {
  activePage: string
  compact?: boolean
  onActivePagePress?: () => void
}

function SideNav({ activePage, compact, onActivePagePress }: SideNavProps) {
  return (
    <Box
      flexDirection={{ mobile: 'row', tablet: 'column' }}
      gap='02'
      px='08'
      {...accessibilityLabel('Nav', 'Main navigation')}
    >
      {navItems.map(item => (
        <SideNavLink
          compact={compact}
          isActive={item.key === activePage}
          item={item}
          key={item.key}
          onActivePress={onActivePagePress}
        />
      ))}
    </Box>
  )
}

export default SideNav
